import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  BoxProps,
  Button,
  CircularProgress,
  Stack,
  Typography,
  TypographyProps,
} from '@mui/material';
import React, {Fragment, PropsWithChildren} from 'react';

export const MessageContainer = (props: BoxProps) => (
  <Box
    {...props}
    sx={[
      {
        margin: 'auto',
        textAlign: 'center',
        flexShrink: 0,
        px: 4,
        color: 'text.secondary',
      },
      ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
    ]}
  />
);

type EmptyMessageContainerProps = PropsWithChildren<
  {
    icon?: React.ReactElement;
  } & BoxProps
>;

export const EmptyMessageContainer = ({
  icon,
  children,
  ...props
}: EmptyMessageContainerProps) => (
  <MessageContainer {...props}>
    {icon && <Box sx={{fontSize: 48}}>{icon}</Box>}

    {children}
  </MessageContainer>
);

type LoadingMessageProps = {
  suffix?: string;
  progressSize?: number;
  titleTypographyProps?: TypographyProps;
  message?: string;
  subheaderTypographyProps?: TypographyProps;
};

export const LoadingMessage = ({
  suffix = '',
  progressSize,
  titleTypographyProps = {},
  message = '',
  subheaderTypographyProps = {},
}: LoadingMessageProps) => {
  return (
    <MessageContainer data-testid="loading-message">
      <Box sx={{marginBottom: 2, width: '100%'}}>
        <CircularProgress color="primary" size={progressSize} />
      </Box>

      <Typography
        variant="h5"
        color="inherit"
        gutterBottom
        {...titleTypographyProps}
      >
        {message ? message : `Loading ${suffix}`}
      </Typography>

      <Typography variant="h6" color="inherit" {...subheaderTypographyProps}>
        One moment please
      </Typography>
    </MessageContainer>
  );
};

type ErrorMessageProps = {
  error?: string;
  message?: string;
  icon?: boolean;
  children?: React.ReactNode;
  componentName?: string;
  onRetry?: (...args: unknown[]) => void;
} & React.HTMLAttributes<HTMLDivElement>;

export const ErrorMessage = ({
  error,
  message,
  icon = true,
  children,
  componentName = 'page',
  onRetry,
}: ErrorMessageProps) => {
  return (
    <EmptyMessageContainer
      icon={
        icon ? (
          <ErrorOutlineOutlinedIcon fontSize="inherit" color="error" />
        ) : undefined
      }
      data-testid="error-message"
    >
      {children ? (
        children
      ) : (
        <Fragment>
          <Typography variant="h6" color="inherit" gutterBottom>
            Houston, we have a problem...
          </Typography>

          {message && (
            <Typography variant="h6" color="inherit" gutterBottom>
              {message}
            </Typography>
          )}

          {error && (
            <Typography variant="overline" color="inherit" gutterBottom>
              {error}
            </Typography>
          )}

          <Box sx={{marginTop: 4}}>
            <Typography variant="h6" color="inherit" gutterBottom>
              Try reloading the {componentName} or let us know what happened.
            </Typography>
          </Box>

          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              gap: 2,
              justifyContent: 'center',
              marginTop: 2,
            }}
          >
            <Button
              color="primary"
              variant="outlined"
              onClick={onRetry}
              startIcon={<RefreshIcon />}
            >
              Reload
            </Button>

            <Button
              color="primary"
              variant="contained"
              href="https://support.planet.com/hc/en-us/requests/new"
              target="_blank"
              rel="noreferrer noopener"
              endIcon={<OpenInNewIcon fontSize="small" />}
            >
              Report Issue
            </Button>
          </Stack>
        </Fragment>
      )}
    </EmptyMessageContainer>
  );
};

export default MessageContainer;
